import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchSession, signOut, type Session, type SignOutResult } from './session';

export const SESSION_QUERY_KEY = ['session'] as const;

/** Shared by the hook and by route guards, so both read the same cache entry. */
export function sessionQueryOptions() {
  return queryOptions({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => fetchSession(),
    staleTime: 60_000,
  });
}

export interface SessionState {
  readonly session: Session | null | undefined;
  readonly isLoading: boolean;
  /**
   * Revokes the session and clears the cache — the cache clears whether or not
   * the request succeeded. Rejects when the auth function refused; the caller
   * owns what the user is told. The caller sends the browser to `logoutUrl`
   * when there is one.
   */
  readonly signOut: () => Promise<SignOutResult>;
}

export function useSession(): SessionState {
  const queryClient = useQueryClient();
  const query = useQuery(sessionQueryOptions());

  return {
    session: query.data,
    isLoading: query.isPending,
    signOut: async () => {
      try {
        return await signOut();
      } finally {
        // 04: leaving the session clears the cache rather than letting another
        // user's screen be painted from this one's data. In `finally` because a
        // sign-out that failed may still have revoked the session server-side,
        // and either way the answers cached under it are no longer safe to paint.
        queryClient.clear();
      }
    },
  };
}
